import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Form, Table, Button } from "react-bootstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import Loading from "../../components/LoadingPage/Loading";
import useEcwOrders from "../../Hooks/useEcwOrders";
import "./AssignManufacturer.scss";

interface ManufacturerOption {
  value: string;
  label: string;
}

const AssignManufacturer: React.FC = () => {
  const [items, setItems] = useState<any[]>([]);
  const [manufacturerOptions, setManufacturerOptions] = useState<ManufacturerOption[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [selectedManufacturer, setSelectedManufacturer] = useState<ManufacturerOption | null>(null);
  const [sortColumn, setSortColumn] = useState<string>("name");
  const [sortDirection, setSortDirection] = useState<string>("asc");

  const {
    getItemsWithMissingEcwManufacturerAPI,
    getItemsWithMissingEcwManufacturerLoading,
    validateManufacturerWithEcwAPI,
    validateManufacturerWithEcwLoading,
  } = useEcwOrders();

  const loadItems = useCallback(async () => {
    try {
      const response = await getItemsWithMissingEcwManufacturerAPI();
      if (response) {
        setItems(response.result || response || []);
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  }, [getItemsWithMissingEcwManufacturerAPI]);

  const loadManufacturers = useCallback(async () => {
    try {
      const response = await validateManufacturerWithEcwAPI({ name: null });
      const results = response?.result || response || [];
      const options = results.map((item: any) => ({
        value: item.manufacturerId || item.manufacturerCode,
        label: item.name,
      })).sort((a: ManufacturerOption, b: ManufacturerOption) => a.label.localeCompare(b.label));
      setManufacturerOptions(options);
    } catch (error: any) {
      toast.error(error.message);
    }
  }, [validateManufacturerWithEcwAPI]);

  useEffect(() => {
    loadItems();
    loadManufacturers();
  }, [loadItems, loadManufacturers]);

  const handleSort = (columnName: string) => {
    if (sortColumn === columnName) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(columnName);
      setSortDirection("asc");
    }
  };

  const filteredItems = useMemo(() => {
    let filtered = items;
    if (searchText.trim()) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(
        (item: any) =>
          (item.name || "").toLowerCase().includes(search) ||
          (item.barcode || "").toLowerCase().includes(search) ||
          (item.ndcCode || "").toLowerCase().includes(search)
      );
    }
    const sorted = [...filtered].sort((a, b) => {
      const aVal = (a[sortColumn] ?? "").toString().toLowerCase();
      const bVal = (b[sortColumn] ?? "").toString().toLowerCase();
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [items, searchText, sortColumn, sortDirection]);

  const totalRecords = filteredItems.length;
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredItems.slice(start, start + perPage);
  }, [filteredItems, currentPage, perPage]);

  const handleSelectAll = (checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      paginatedItems.forEach((item: any) => newSelected.add(item.id));
    } else {
      paginatedItems.forEach((item: any) => newSelected.delete(item.id));
    }
    setSelectedIds(newSelected);
  };

  const handleSelectItem = (id: number) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const isAllPageSelected =
    paginatedItems.length > 0 &&
    paginatedItems.every((item: any) => selectedIds.has(item.id));

  const handleClearSelection = () => {
    setSelectedIds(new Set());
    setSelectedManufacturer(null);
  };

  const handleCancel = () => {
    setSelectedIds(new Set());
    setSelectedManufacturer(null);
  };

  const handleAssign = () => {
    alert(
      `Assigning "${selectedManufacturer?.label}" to ${selectedIds.size} item(s).`
    );
  };

  return (
    <div className="assign-manufacturer-wrapper">
      {(getItemsWithMissingEcwManufacturerLoading || validateManufacturerWithEcwLoading) && <Loading />}
      {/* Page Header */}
      <div className="assign-manufacturer-header">
        <div className="listing-title-info">
          <h2>List of ECW-Unmatched Manufacturer Items</h2>
          {/* <p>Select a manufacturer and assign it to multiple items from the list.</p> */}
        </div>
        <div className="assign-manufacturer-search-controls">
          <div className="listing-searchbar-item-box">
            <input
              type="text"
              className="form-control"
              placeholder="Search items by name, SKU, NDC..."
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setCurrentPage(1);
              }}
              autoFocus
              onFocus={(e) => e.target.select()}
            />
          </div>
          <Button
            variant="outline-secondary"
            className="btn-search"
            onClick={handleClearSelection}
          >
            Clear Selection
          </Button>
        </div>
        <div className="mc-page-size">
          <label htmlFor="assignPageSize">Page Size:</label>
          <input
            id="assignPageSize"
            type="number"
            className="form-control"
            value={perPage}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              if (val > 0) {
                setPerPage(val);
                setCurrentPage(1);
              }
            }}
          />
        </div>
      </div>

      {/* Items Table s*/}
      <div className="listing-table-wrapper">
        <Table responsive="md" hover>
          <thead>
            <tr>
              <th className="checkbox-col">
                <Form.Check
                  type="checkbox"
                  checked={isAllPageSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </th>
              <th className="cursor-pointer" onClick={() => handleSort('name')}>Item Name {sortColumn === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
              <th className="cursor-pointer" onClick={() => handleSort('barcode')}>Barcode {sortColumn === 'barcode' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
              <th className="cursor-pointer" onClick={() => handleSort('manufacturer')}>Current Manufacturer {sortColumn === 'manufacturer' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
              <th className="cursor-pointer" onClick={() => handleSort('ndcCode')}>NDC {sortColumn === 'ndcCode' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
              <th className="cursor-pointer" onClick={() => handleSort('category')}>Category {sortColumn === 'category' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
              <th className="cursor-pointer" onClick={() => handleSort('isProduct')}>Type {sortColumn === 'isProduct' && (sortDirection === 'asc' ? '↑' : '↓')}</th>
            </tr>
          </thead>
          <tbody>
            {paginatedItems.length > 0 ? (
              paginatedItems.map((item: any) => (
                <tr
                  key={item.id}
                  className={selectedIds.has(item.id) ? "selected" : ""}
                  onClick={() => handleSelectItem(item.id)}
                >
                  <td className="checkbox-col">
                    <Form.Check
                      type="checkbox"
                      checked={selectedIds.has(item.id)}
                      onChange={() => handleSelectItem(item.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td>{item.name}</td>
                  <td>{item.barcode}</td>
                  <td>{item.manufacturer || <span className="text-muted">&mdash;</span>}</td>
                  <td>{item.ndcCode}</td>
                  <td>{item.category}</td>
                  <td>{item.isProduct ? 'Product Master' : 'Items'}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="text-center">
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Bottom Action Bar */}
      <div className="assign-manufacturer-bottom-bar">
        <div className="bottom-bar-content">
          <span className="selected-count">
            {selectedIds.size} item{selectedIds.size !== 1 ? "s" : ""} selected
          </span>
          <div className="manufacturer-dropdown-wrapper">
            <Select
              options={manufacturerOptions}
              value={selectedManufacturer}
              onChange={(option) => setSelectedManufacturer(option)}
              placeholder="Search and select manufacturer..."
              isClearable
              classNamePrefix="manufacturer-select"
            />
          </div>
          <div className="bottom-bar-actions">
            <Button variant="outline-secondary" className="cancel-btn" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant="primary"
              className="assign-btn"
              onClick={handleAssign}
              disabled={!selectedManufacturer}
            >
              <i className="bi bi-link-45deg"></i> Assign Manufacturer
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignManufacturer;
